import { useEffect, useReducer, useRef } from 'react'
import { history, useTypedSelector } from '../../redux/store'
import {
  isResponseCodeSuccess,
  getSupplierId,
  countToStr,
  orderTypeData,
} from '../../tools/util'
import { GET_TODOLIST, GET_TODOLIST_COUNT } from '../../api/CommonApi'
import { useSkipInitEffect } from '../../tools/utilHooks'
import { setModuleDataAction } from '../../modules/pagecache'
import { useDispatch } from 'react-redux'
import { SUPROUTE } from '../../route'
import reducer, {
  initialState,
  invoiceTabKey,
  reloadStateAction,
  setCheckedRadioKeyAction,
  setCurrentRadioDictAction,
  setDataSourceAction,
  setIsTableLoadingAction,
  setPaginationAction,
  setSearchParamAction,
} from './reducer'
import { useAliveController } from 'react-activation'
import { TodoListRes } from '../../api/CommonApi'
import { TODO_CATEGORY } from '../../global/enum/common'

// 跳转订单详情
export const jumpApplicationDetail = (record: any) => {
  history.replace({
    pathname: `${SUPROUTE}/invoiceApplicationDetail`,
    state: {
      orderId: record.id,
      applicationNumber: record.invoiceApplicationNumber,
    },
  })
}

/** 待办事项根据 category 跳转对应页面 */
export const handleTodoNavigation = async (
  item: TodoListRes,
  drop: (name: string) => Promise<boolean>,
) => {
  const jumpParams = item.jumpParams ? JSON.parse(item.jumpParams) : {}

  switch (item.category) {
    // 询价单
    case TODO_CATEGORY.INQUIRY:
      await drop('inquiryDetail')
      history.push({
        pathname: `${SUPROUTE}/inquiryDetail`,
        search: `id=${jumpParams.id}`,
      })
      break
    // 需发货 → 客户订单详情
    case TODO_CATEGORY.NEED_SHIP:
      await drop('orderdetail')
      history.push({
        pathname: `${SUPROUTE}/orderdetail`,
        search: `id=${jumpParams.id}`,
      })
      break
    // 需上传凭证 → 客户订单详情
    case TODO_CATEGORY.NEED_UPLOAD_VOUCHER:
      await drop('orderdetail')
      history.push({
        pathname: `${SUPROUTE}/orderdetail`,
        search: `id=${jumpParams.id}`,
      })
      break
    // 凭证驳回 → 客户订单详情
    case TODO_CATEGORY.VOUCHER_REJECT:
      await drop('orderdetail')
      history.push({
        pathname: `${SUPROUTE}/orderdetail`,
        search: `id=${jumpParams.id}`,
      })
      break
    // 开票(待开发票) → 待开发票列表页
    case TODO_CATEGORY.PENDING_INVOICE:
      await drop('pendingInvoicePage')
      history.push({
        pathname: `${SUPROUTE}/pendingInvoicePage`,
      })
      break
    // 发票驳回(电商) → 电商供应商进项票详情页
    case TODO_CATEGORY.INVOICE_REJECT_EC:
      await drop('invoiceDetail')
      history.push({
        pathname: `${SUPROUTE}/invoiceDetail`,
        state: {
          id: jumpParams.id,
          type: orderTypeData.find((item) => item.code === jumpParams.type)
            ?.des,
          titleOfTheContract: jumpParams.titleOfTheContract,
        },
      })
      break
    // 开票(发票录入) → 发票录入列表页
    case TODO_CATEGORY.INVOICE_ENTRY:
      await drop('invoiceEntry')
      history.push({
        pathname: `${SUPROUTE}/invoiceEntry`,
        search: 'tab=CUSTOMER',
      })
      break
    // 待接单 → 采购订单详情
    case TODO_CATEGORY.PENDING_ORDER:
      await drop('ordersupplierdetail')
      history.push({
        pathname: `${SUPROUTE}/ordersupplierdetail`,
        search: `id=${jumpParams.id}`,
      })
      break
    // 取消订货确认 → 采购订单详情-取消明细tab
    case TODO_CATEGORY.CANCEL_ORDER_CONFIRM:
      await drop('ordersupplierdetail')
      history.push({
        pathname: `${SUPROUTE}/ordersupplierdetail`,
        search: `id=${jumpParams.id}&tab=cancel&contractId=${item.id}`,
      })
      break
    // 发票驳回(自营) → 供应商进项票详情页
    case TODO_CATEGORY.INVOICE_REJECT_SELF:
      await drop('invoiceDetail')
      history.push({
        pathname: `${SUPROUTE}/invoiceDetail`,
        state: {
          id: jumpParams.id,
          type: orderTypeData.find((item) => item.code === jumpParams.type)
            ?.des,
          titleOfTheContract: jumpParams.titleOfTheContract,
        },
      })
      break
    // 物料驳回 → 物料申请详情页
    case TODO_CATEGORY.MATERIAL_REJECT:
      await drop('launchgoodsInfo')
      history.push({
        pathname: `${SUPROUTE}/launchgoodsInfo`,
        state: {
          productId: jumpParams.id || jumpParams.id,
          productMdmId: jumpParams.productMdmId,
          BtnState: ['Edit', 'AssessReject'],
          // 审核来源（资质场景）改由详情返参 auditType 决定（详情页 setDetailAuditType 推算）
        },
      })
      break
    // 品牌驳回 → 品牌申请详情页
    case TODO_CATEGORY.BRAND_REJECT:
      await drop('editBrand')
      history.push({
        pathname: `${SUPROUTE}/editBrand`,
        state: {
          brandId: jumpParams.brandId,
          BtnState: ['Edit'],
        },
      })
      break
    default:
      break
  }
}

const useToDoListHook = () => {
  const { drop } = useAliveController()
  const [state, dispatch] = useReducer(reducer, initialState)
  const { checkedRadioKey, currentRadioDict, pagination, searchParam } = state
  const dispatchredux = useDispatch()
  const { pagekey } = useTypedSelector((state) => state.pageCache)
  const CACHE_KEY = history.location.pathname

  // 标记是否已完成初始化
  const isInitializedRef = useRef(false)
  // 标记是否已通过 defaultSchemeId 触发请求
  const hasDefaultSchemeRequestRef = useRef(false)

  useEffect(() => {
    const flag = getpagecache()
    if (flag) {
      const action = reloadStateAction(flag)
      dispatch(action)
      getNewData(flag.checkedRadioKey, flag.searchParam).then(() => {
        isInitializedRef.current = true
      })
    } else {
      checkPendingData().then(() => {
        // 如果没有通过 defaultSchemeId 触发请求，则标记为已初始化
        if (!hasDefaultSchemeRequestRef.current) {
          isInitializedRef.current = true
        }
      })
    }
  }, [])

  const handleCheckedChange = async (key: invoiceTabKey) => {
    dispatch(setCheckedRadioKeyAction(key))
    const res = await GET_TODOLIST_COUNT({ supplierId: getSupplierId() })
    if (isResponseCodeSuccess(res.code)) {
      const resData = res.data
      const newRadioDict = { ...currentRadioDict }
      newRadioDict['当前待办']!.value =
        `当前待办(${countToStr(resData.currentToDoCount)})`
      dispatch(setCurrentRadioDictAction(newRadioDict))
    }
    handleTodoListRequest({
      siftTab: currentRadioDict[key]?.keyName,
      pageNo: 1,
      pageSize: pagination.pagesize,
    })
  }

  const handleSearchClick = (value: any) => {
    // 如果是初始化阶段且有 schemeId，优先使用 defaultSchemeId 请求
    if (!isInitializedRef.current && value.schemeId) {
      hasDefaultSchemeRequestRef.current = true
      // 先获取 count，然后使用 schemeId 请求数据
      initWithSchemeId(value)
      return
    }

    dispatch(setSearchParamAction(value))
    handleTodoListRequest(
      {
        ...value,
        pageNo: 1,
        pageSize: pagination.pagesize,
      },
      true,
    )
  }

  const handlePaginationChange = (page: number, pageSize?: number) => {
    handleTodoListRequest({
      pageNo: page,
      pageSize: pageSize ? pageSize : pagination.pagesize,
    })
    dispatch(
      setPaginationAction({
        ...pagination,
        pagesize: pageSize ? pageSize : pagination.pagesize,
        current: page,
      }),
    )
  }

  const handleTodoListRequest = async (
    param: { pageNo?: number; pageSize?: number; [key: string]: any },
    isSearch?: boolean,
    targetTabKey?: invoiceTabKey, // 可选参数：指定要请求的tab
  ) => {
    const searchParams = isSearch ? {} : { ...searchParam }
    const tabKey = targetTabKey || checkedRadioKey
    const tabKeyName = currentRadioDict[tabKey]?.keyName || ''
    dispatch(setIsTableLoadingAction(true))
    const res = await GET_TODOLIST({
      supplierId: getSupplierId(),
      ...searchParams,
      ...param,
      pageNo: param.pageNo || pagination.current,
      pageSize: param.pageSize || pagination.pagesize,
      siftTab:
        param.siftTab !== undefined
          ? param.siftTab
          : currentRadioDict[checkedRadioKey]?.keyName, // 根据选中的tab传递状态参数
    })
    if (isResponseCodeSuccess(res.code)) {
      const resData = res.data
      // 设置当前表格数据
      dispatch(setDataSourceAction(resData.content || []))
      // 设置当前页面数据量
      const total = resData.totalCount || 0
      dispatch(
        setPaginationAction({
          pagesize: resData.pageSize || pagination.pagesize,
          current: resData.pageNo || pagination.current,
          total: total,
        }),
      )
    } else {
      // API 返回错误时，设置空数据
      console.error('待办事项API返回错误:', res)
      dispatch(setDataSourceAction([]))
      dispatch(
        setPaginationAction({
          ...pagination,
          total: 0,
        }),
      )
    }
    dispatch(setIsTableLoadingAction(false))
  }

  const handleTableRefresh = async () => {
    handleTodoListRequest({
      pageNo: 1,
      pageSize: pagination.pagesize,
    })
    dispatch(
      setPaginationAction({
        ...pagination,
        current: 1,
      }),
    )
  }

  const getNewData = async (tabKey: invoiceTabKey, searchParam: any) => {
    try {
      dispatch(setIsTableLoadingAction(true))
      const res = await GET_TODOLIST_COUNT({ supplierId: getSupplierId() })
      if (isResponseCodeSuccess(res.code)) {
        const resData = res.data
        const newRadioDict = { ...currentRadioDict }
        newRadioDict['当前待办']!.value =
          `当前待办(${countToStr(resData.currentToDoCount)})`
        dispatch(setCurrentRadioDictAction(newRadioDict))

        dispatch(setCheckedRadioKeyAction(tabKey))
        handleTodoListRequest(
          {
            ...searchParam,
            siftTab: currentRadioDict[tabKey]?.keyName,
            pageNo: 1,
            pageSize: pagination.pagesize,
          },
          true,
        )
      }
    } finally {
      isInitializedRef.current = true
    }
  }

  // 使用 defaultSchemeId 初始化
  const initWithSchemeId = async (value: any) => {
    try {
      dispatch(setIsTableLoadingAction(true))
      // 先获取 count 更新 tab 显示
      const res = await GET_TODOLIST_COUNT({ supplierId: getSupplierId() })
      let currentToDoCount = 0
      if (isResponseCodeSuccess(res.code)) {
        const resData = res.data
        currentToDoCount = resData.currentToDoCount || 0
        const newRadioDict = { ...currentRadioDict }
        newRadioDict['当前待办']!.value =
          `当前待办(${countToStr(currentToDoCount)})`
        dispatch(setCurrentRadioDictAction(newRadioDict))
      }

      // 保存搜索参数（包含 schemeId）
      dispatch(setSearchParamAction(value))

      // 使用 defaultSchemeId 请求数据，默认显示"当前待办"
      const defaultTabKey = currentToDoCount > 0 ? '当前待办' : '已完结'
      dispatch(setCheckedRadioKeyAction(defaultTabKey))

      handleTodoListRequest(
        {
          ...value,
          siftTab: currentRadioDict[defaultTabKey]?.keyName,
          pageNo: 1,
          pageSize: pagination.pagesize,
        },
        true,
      )
    } finally {
      isInitializedRef.current = true
    }
  }

  // 先请求"当前待办"的数据，只获取总数
  const checkPendingData = async () => {
    try {
      dispatch(setIsTableLoadingAction(true))
      const res = await GET_TODOLIST_COUNT({ supplierId: getSupplierId() })
      if (isResponseCodeSuccess(res.code)) {
        const resData = res.data
        const newRadioDict = { ...currentRadioDict }
        newRadioDict['当前待办']!.value =
          `当前待办(${countToStr(resData.currentToDoCount)})`
        dispatch(setCurrentRadioDictAction(newRadioDict))

        // 如果已经有 defaultSchemeId 的请求，则不再执行数据请求
        if (hasDefaultSchemeRequestRef.current) {
          return
        }

        if (res.data.currentToDoCount > 0) {
          dispatch(setCheckedRadioKeyAction('当前待办'))
          handleTodoListRequest(
            {
              ...searchParam,
              siftTab: currentRadioDict['当前待办']?.keyName,
              pageNo: 1,
              pageSize: pagination.pagesize,
            },
            true,
          )
        } else {
          dispatch(setCheckedRadioKeyAction('已完结'))
          handleTodoListRequest(
            {
              ...searchParam,
              siftTab: currentRadioDict['已完结']?.keyName,
              pageNo: 1,
              pageSize: pagination.pagesize,
            },
            true,
          )
        }
      }
    } finally {
      // 如果没有通过 defaultSchemeId 触发请求，则标记为已初始化
      if (!hasDefaultSchemeRequestRef.current) {
        isInitializedRef.current = true
      }
    }
  }
  useSkipInitEffect(() => {
    // 缓存存数据
    const action = setModuleDataAction({
      key: CACHE_KEY,
      data: state,
    })
    dispatchredux(action)
    // eslint-disable-next-line
  }, [state])

  // 获取数据
  const getpagecache = () => {
    const data = pagekey.filter((item) => item.key === CACHE_KEY)
    if (data.length === 1) {
      return data[0].data
    }
    return null
  }

  const handleProcess = async (item: TodoListRes) => {
    await handleTodoNavigation(item, drop)
  }
  return {
    hookstate: state,
    handleCheckedChange,
    handlePaginationChange,
    handleSearchClick,
    handleTableRefresh,
    handleProcess,
  }
}
export default useToDoListHook
import { useEffect, useReducer, useRef } from 'react'
import { history, useTypedSelector } from '../../redux/store'
import {
  isResponseCodeSuccess,
  getSupplierId,
  countToStr,
  orderTypeData,
} from '../../tools/util'
import { GET_TODOLIST, GET_TODOLIST_COUNT } from '../../api/CommonApi'
import { useSkipInitEffect } from '../../tools/utilHooks'
import { setModuleDataAction } from '../../modules/pagecache'
import { useDispatch } from 'react-redux'
import { SUPROUTE } from '../../route'
import reducer, {
  initialState,
  invoiceTabKey,
  reloadStateAction,
  setCheckedRadioKeyAction,
  setCurrentRadioDictAction,
  setDataSourceAction,
  setIsTableLoadingAction,
  setPaginationAction,
  setSearchParamAction,
} from './reducer'
import { useAliveController } from 'react-activation'
import { TodoListRes } from '../../api/CommonApi'
import { TODO_CATEGORY } from '../../global/enum/common'

// 跳转订单详情
export const jumpApplicationDetail = (record: any) => {
  history.replace({
    pathname: `${SUPROUTE}/invoiceApplicationDetail`,
    state: {
      orderId: record.id,
      applicationNumber: record.invoiceApplicationNumber,
    },
  })
}

/** 待办事项根据 category 跳转对应页面 */
export const handleTodoNavigation = async (
  item: TodoListRes,
  drop: (name: string) => Promise<boolean>,
) => {
  const jumpParams = item.jumpParams ? JSON.parse(item.jumpParams) : {}

  switch (item.category) {
    // 询价单
    case TODO_CATEGORY.INQUIRY:
      await drop('inquiryDetail')
      history.push({
        pathname: `${SUPROUTE}/inquiryDetail`,
        search: `id=${jumpParams.id}`,
      })
      break
    // 需发货 → 客户订单详情
    case TODO_CATEGORY.NEED_SHIP:
      await drop('orderdetail')
      history.push({
        pathname: `${SUPROUTE}/orderdetail`,
        search: `id=${jumpParams.id}`,
      })
      break
    // 需上传凭证 → 客户订单详情
    case TODO_CATEGORY.NEED_UPLOAD_VOUCHER:
      await drop('orderdetail')
      history.push({
        pathname: `${SUPROUTE}/orderdetail`,
        search: `id=${jumpParams.id}`,
      })
      break
    // 凭证驳回 → 客户订单详情
    case TODO_CATEGORY.VOUCHER_REJECT:
      await drop('orderdetail')
      history.push({
        pathname: `${SUPROUTE}/orderdetail`,
        search: `id=${jumpParams.id}`,
      })
      break
    // 开票(待开发票) → 待开发票列表页
    case TODO_CATEGORY.PENDING_INVOICE:
      await drop('pendingInvoicePage')
      history.push({
        pathname: `${SUPROUTE}/pendingInvoicePage`,
      })
      break
    // 发票驳回(电商) → 电商供应商进项票详情页
    case TODO_CATEGORY.INVOICE_REJECT_EC:
      await drop('invoiceDetail')
      history.push({
        pathname: `${SUPROUTE}/invoiceDetail`,
        state: {
          id: jumpParams.id,
          type: orderTypeData.find((item) => item.code === jumpParams.type)
            ?.des,
          titleOfTheContract: jumpParams.titleOfTheContract,
        },
      })
      break
    // 开票(发票录入) → 发票录入列表页
    case TODO_CATEGORY.INVOICE_ENTRY:
      await drop('invoiceEntry')
      history.push({
        pathname: `${SUPROUTE}/invoiceEntry`,
        search: 'tab=CUSTOMER',
      })
      break
    // 待接单 → 采购订单详情
    case TODO_CATEGORY.PENDING_ORDER:
      await drop('ordersupplierdetail')
      history.push({
        pathname: `${SUPROUTE}/ordersupplierdetail`,
        search: `id=${jumpParams.id}`,
      })
      break
    // 取消订货确认 → 采购订单详情-取消明细tab
    case TODO_CATEGORY.CANCEL_ORDER_CONFIRM:
      await drop('ordersupplierdetail')
      history.push({
        pathname: `${SUPROUTE}/ordersupplierdetail`,
        search: `id=${jumpParams.id}&tab=cancel&contractId=${item.id}`,
      })
      break
    // 发票驳回(自营) → 供应商进项票详情页
    case TODO_CATEGORY.INVOICE_REJECT_SELF:
      await drop('invoiceDetail')
      history.push({
        pathname: `${SUPROUTE}/invoiceDetail`,
        state: {
          id: jumpParams.id,
          type: orderTypeData.find((item) => item.code === jumpParams.type)
            ?.des,
          titleOfTheContract: jumpParams.titleOfTheContract,
        },
      })
      break
    // 物料驳回 → 物料申请详情页
    case TODO_CATEGORY.MATERIAL_REJECT:
      await drop('launchgoodsInfo')
      history.push({
        pathname: `${SUPROUTE}/launchgoodsInfo`,
        state: {
          productId: jumpParams.id || jumpParams.id,
          productMdmId: jumpParams.productMdmId,
          BtnState: ['Edit', 'AssessReject'],
          // 审核来源（资质场景）改由详情返参 auditType 决定（详情页 setDetailAuditType 推算）
        },
      })
      break
    // 品牌驳回 → 品牌申请详情页
    case TODO_CATEGORY.BRAND_REJECT:
      await drop('editBrand')
      history.push({
        pathname: `${SUPROUTE}/editBrand`,
        state: {
          brandId: jumpParams.brandId,
          BtnState: ['Edit'],
        },
      })
      break
    default:
      break
  }
}

const useToDoListHook = () => {
  const { drop } = useAliveController()
  const [state, dispatch] = useReducer(reducer, initialState)
  const { checkedRadioKey, currentRadioDict, pagination, searchParam } = state
  const dispatchredux = useDispatch()
  const { pagekey } = useTypedSelector((state) => state.pageCache)
  const CACHE_KEY = history.location.pathname

  // 标记是否已完成初始化
  const isInitializedRef = useRef(false)
  // 标记是否已通过 defaultSchemeId 触发请求
  const hasDefaultSchemeRequestRef = useRef(false)

  useEffect(() => {
    const flag = getpagecache()
    if (flag) {
      const action = reloadStateAction(flag)
      dispatch(action)
      getNewData(flag.checkedRadioKey, flag.searchParam).then(() => {
        isInitializedRef.current = true
      })
    } else {
      checkPendingData().then(() => {
        // 如果没有通过 defaultSchemeId 触发请求，则标记为已初始化
        if (!hasDefaultSchemeRequestRef.current) {
          isInitializedRef.current = true
        }
      })
    }
  }, [])

  const handleCheckedChange = async (key: invoiceTabKey) => {
    dispatch(setCheckedRadioKeyAction(key))
    const res = await GET_TODOLIST_COUNT({ supplierId: getSupplierId() })
    if (isResponseCodeSuccess(res.code)) {
      const resData = res.data
      const newRadioDict = { ...currentRadioDict }
      newRadioDict['当前待办']!.value =
        `当前待办(${countToStr(resData.currentToDoCount)})`
      dispatch(setCurrentRadioDictAction(newRadioDict))
    }
    handleTodoListRequest({
      siftTab: currentRadioDict[key]?.keyName,
      pageNo: 1,
      pageSize: pagination.pagesize,
    })
  }

  const handleSearchClick = (value: any) => {
    // 如果是初始化阶段且有 schemeId，优先使用 defaultSchemeId 请求
    if (!isInitializedRef.current && value.schemeId) {
      hasDefaultSchemeRequestRef.current = true
      // 先获取 count，然后使用 schemeId 请求数据
      initWithSchemeId(value)
      return
    }

    dispatch(setSearchParamAction(value))
    handleTodoListRequest(
      {
        ...value,
        pageNo: 1,
        pageSize: pagination.pagesize,
      },
      true,
    )
  }

  const handlePaginationChange = (page: number, pageSize?: number) => {
    handleTodoListRequest({
      pageNo: page,
      pageSize: pageSize ? pageSize : pagination.pagesize,
    })
    dispatch(
      setPaginationAction({
        ...pagination,
        pagesize: pageSize ? pageSize : pagination.pagesize,
        current: page,
      }),
    )
  }

  const handleTodoListRequest = async (
    param: { pageNo?: number; pageSize?: number; [key: string]: any },
    isSearch?: boolean,
    targetTabKey?: invoiceTabKey, // 可选参数：指定要请求的tab
  ) => {
    const searchParams = isSearch ? {} : { ...searchParam }
    const tabKey = targetTabKey || checkedRadioKey
    const tabKeyName = currentRadioDict[tabKey]?.keyName || ''
    dispatch(setIsTableLoadingAction(true))
    const res = await GET_TODOLIST({
      supplierId: getSupplierId(),
      ...searchParams,
      ...param,
      pageNo: param.pageNo || pagination.current,
      pageSize: param.pageSize || pagination.pagesize,
      siftTab:
        param.siftTab !== undefined
          ? param.siftTab
          : currentRadioDict[checkedRadioKey]?.keyName, // 根据选中的tab传递状态参数
    })
    if (isResponseCodeSuccess(res.code)) {
      const resData = res.data
      // 设置当前表格数据
      dispatch(setDataSourceAction(resData.content || []))
      // 设置当前页面数据量
      const total = resData.totalCount || 0
      dispatch(
        setPaginationAction({
          pagesize: resData.pageSize || pagination.pagesize,
          current: resData.pageNo || pagination.current,
          total: total,
        }),
      )
    } else {
      // API 返回错误时，设置空数据
      console.error('待办事项API返回错误:', res)
      dispatch(setDataSourceAction([]))
      dispatch(
        setPaginationAction({
          ...pagination,
          total: 0,
        }),
      )
    }
    dispatch(setIsTableLoadingAction(false))
  }

  const handleTableRefresh = async () => {
    handleTodoListRequest({
      pageNo: 1,
      pageSize: pagination.pagesize,
    })
    dispatch(
      setPaginationAction({
        ...pagination,
        current: 1,
      }),
    )
  }

  const getNewData = async (tabKey: invoiceTabKey, searchParam: any) => {
    try {
      dispatch(setIsTableLoadingAction(true))
      const res = await GET_TODOLIST_COUNT({ supplierId: getSupplierId() })
      if (isResponseCodeSuccess(res.code)) {
        const resData = res.data
        const newRadioDict = { ...currentRadioDict }
        newRadioDict['当前待办']!.value =
          `当前待办(${countToStr(resData.currentToDoCount)})`
        dispatch(setCurrentRadioDictAction(newRadioDict))

        dispatch(setCheckedRadioKeyAction(tabKey))
        handleTodoListRequest(
          {
            ...searchParam,
            siftTab: currentRadioDict[tabKey]?.keyName,
            pageNo: 1,
            pageSize: pagination.pagesize,
          },
          true,
        )
      }
    } finally {
      isInitializedRef.current = true
    }
  }

  // 使用 defaultSchemeId 初始化
  const initWithSchemeId = async (value: any) => {
    try {
      dispatch(setIsTableLoadingAction(true))
      // 先获取 count 更新 tab 显示
      const res = await GET_TODOLIST_COUNT({ supplierId: getSupplierId() })
      let currentToDoCount = 0
      if (isResponseCodeSuccess(res.code)) {
        const resData = res.data
        currentToDoCount = resData.currentToDoCount || 0
        const newRadioDict = { ...currentRadioDict }
        newRadioDict['当前待办']!.value =
          `当前待办(${countToStr(currentToDoCount)})`
        dispatch(setCurrentRadioDictAction(newRadioDict))
      }

      // 保存搜索参数（包含 schemeId）
      dispatch(setSearchParamAction(value))

      // 使用 defaultSchemeId 请求数据，默认显示"当前待办"
      const defaultTabKey = currentToDoCount > 0 ? '当前待办' : '已完结'
      dispatch(setCheckedRadioKeyAction(defaultTabKey))

      handleTodoListRequest(
        {
          ...value,
          siftTab: currentRadioDict[defaultTabKey]?.keyName,
          pageNo: 1,
          pageSize: pagination.pagesize,
        },
        true,
      )
    } finally {
      isInitializedRef.current = true
    }
  }

  // 先请求"当前待办"的数据，只获取总数
  const checkPendingData = async () => {
    try {
      dispatch(setIsTableLoadingAction(true))
      const res = await GET_TODOLIST_COUNT({ supplierId: getSupplierId() })
      if (isResponseCodeSuccess(res.code)) {
        const resData = res.data
        const newRadioDict = { ...currentRadioDict }
        newRadioDict['当前待办']!.value =
          `当前待办(${countToStr(resData.currentToDoCount)})`
        dispatch(setCurrentRadioDictAction(newRadioDict))

        // 如果已经有 defaultSchemeId 的请求，则不再执行数据请求
        if (hasDefaultSchemeRequestRef.current) {
          return
        }

        if (res.data.currentToDoCount > 0) {
          dispatch(setCheckedRadioKeyAction('当前待办'))
          handleTodoListRequest(
            {
              ...searchParam,
              siftTab: currentRadioDict['当前待办']?.keyName,
              pageNo: 1,
              pageSize: pagination.pagesize,
            },
            true,
          )
        } else {
          dispatch(setCheckedRadioKeyAction('已完结'))
          handleTodoListRequest(
            {
              ...searchParam,
              siftTab: currentRadioDict['已完结']?.keyName,
              pageNo: 1,
              pageSize: pagination.pagesize,
            },
            true,
          )
        }
      }
    } finally {
      // 如果没有通过 defaultSchemeId 触发请求，则标记为已初始化
      if (!hasDefaultSchemeRequestRef.current) {
        isInitializedRef.current = true
      }
    }
  }
  useSkipInitEffect(() => {
    // 缓存存数据
    const action = setModuleDataAction({
      key: CACHE_KEY,
      data: state,
    })
    dispatchredux(action)
    // eslint-disable-next-line
  }, [state])

  // 获取数据
  const getpagecache = () => {
    const data = pagekey.filter((item) => item.key === CACHE_KEY)
    if (data.length === 1) {
      return data[0].data
    }
    return null
  }

  const handleProcess = async (item: TodoListRes) => {
    await handleTodoNavigation(item, drop)
  }
  return {
    hookstate: state,
    handleCheckedChange,
    handlePaginationChange,
    handleSearchClick,
    handleTableRefresh,
    handleProcess,
  }
}
export default useToDoListHook
